import * as React from "react";
import { useState, useEffect } from "react";
import { api } from "../api";
import { RouteComponentProps } from "react-router";
import { RLink } from "../DesignSystem/RLink";
import { TwoColumnView, Page } from "../DesignSystem/formatting/TwoColumnView";
import { FeedPanel } from "../DesignSystem/formatting/FeedComponents";
import { EntryCompoJSON, ChipsAPI } from "../../../src/apidefs";
import { HeartIcon } from "@heroicons/react/24/solid";

type AllFavoritesViewProps = RouteComponentProps<{ username: string }>;

type StatsResponse = Exclude<
  ChipsAPI["/api/user/:username/favorites/stats"]["GET"]["response"],
  { type: "error" | "success" }
>;

const StatsPanel = ({ stats }: { stats: StatsResponse | null }) => {
  if (!stats) {
    return (
      <FeedPanel title={<>Favorites Statistics</>}>
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </FeedPanel>
    );
  }

  const userCreatedDate = new Date(stats.userCreated);
  const now = new Date();
  const daysSinceSignup = Math.max(
    1,
    Math.floor((now.getTime() - userCreatedDate.getTime()) / (1000 * 60 * 60 * 24))
  );
  const avgFavoritesPerDay = (stats.totalFavorites / daysSinceSignup).toFixed(2);

  const statItems = [
    { label: "Total Favorites", value: stats.totalFavorites.toLocaleString() },
    { label: "Avg. Favorites per Day", value: avgFavoritesPerDay },
  ];

  return (
    <>
      <FeedPanel title={<>Favorites Statistics</>}>
        <div className="space-y-3">
          {statItems.map((stat) => (
            <div
              key={stat.label}
              className="flex items-center justify-between text-sm"
            >
              <span className="text-slate-500 dark:text-slate-400">
                {stat.label}
              </span>
              <span className="font-medium">{stat.value}</span>
            </div>
          ))}
        </div>
      </FeedPanel>

      {stats.topAuthors.length > 0 && (
        <FeedPanel title={<>Top Liked Artists</>}>
          <div className="space-y-3">
            {stats.topAuthors.map((author, index) => (
              <div key={author.userId} className="flex items-center gap-3">
                <span className="w-5 text-sm font-medium text-slate-400 dark:text-slate-500">
                  {index + 1}.
                </span>
                <img
                  src={author.avatarUrl}
                  alt={author.username}
                  className="h-8 w-8 rounded-full object-cover"
                />
                <div className="min-w-0 flex-1">
                  <RLink
                    to={`/user/${author.username}`}
                    className="block truncate font-medium hover:underline"
                  >
                    {author.username}
                  </RLink>
                </div>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {author.favoriteCount.toLocaleString()} {author.favoriteCount === 1 ? "like" : "likes"}
                </span>
              </div>
            ))}
          </div>
        </FeedPanel>
      )}
    </>
  );
};

export default function AllFavoritesView(
  props: AllFavoritesViewProps
): JSX.Element {
  const username = props.match.params.username;
  const [favorites, setFavorites] = useState<EntryCompoJSON[]>([]);
  const [stats, setStats] = useState<StatsResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [userResult, statsResult] = await Promise.all([
        api.get<"/api/user/:username">(`/api/user/${username}`),
        api.get<"/api/user/:username/favorites/stats">(
          `/api/user/${username}/favorites/stats`
        ),
      ]);

      if (!api.isToast(userResult) && !cancelled) {
        setFavorites(userResult.favorites);
      }
      if (!api.isToast(statsResult) && !cancelled) {
        setStats(statsResult);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [username]);

  return (
    <Page>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
          {username}'s favorites
        </h1>
        <RLink
          to={`/user/${username}`}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Return to {username}'s page
        </RLink>
      </div>

      <TwoColumnView
        left={
          <div className="space-y-3">
            {favorites.map((entry) => (
              <RLink
                key={entry.id}
                to={`/entry/${entry.id}`}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-all hover:border-slate-300 hover:shadow-md dark:border-white/10 dark:bg-slate-800 dark:hover:border-white/20"
              >
                <HeartIcon className="h-5 w-5 flex-shrink-0 text-rose-500" />
                <span className="min-w-0 flex-1 truncate font-medium">
                  {entry.title}
                </span>
                <span className="flex-shrink-0 text-sm text-slate-500 dark:text-slate-400">
                  {entry.compo_name}
                </span>
              </RLink>
            ))}
            {favorites.length === 0 && (
              <div className="py-8 text-center text-slate-500 dark:text-slate-400">
                Apparently {username} is a hard person to please!
              </div>
            )}
          </div>
        }
        right={<StatsPanel stats={stats} />}
      />
    </Page>
  );
}
